// src/components/clients/LogActivityDialog.tsx
import React, { useState, useEffect } from "react";
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { supabase } from "../../lib/supabase";
import type { Client } from "./types";
import { allActivityTypes } from "./config";

interface LogActivityDialogProps {
  open: boolean;
  onClose: () => void;
  client: Client | null;
  initialActivityType?: string;
  onSuccess: () => void;
  /**
   * PKT-0410: extra provenance merged into the logged activity's metadata.
   * The mobile Call/Text handoff uses this to keep `launch_mode` /
   * `template_prefilled` on the row now that it logs through this dialog
   * instead of writing at composer launch. `client_name` and `notes` always
   * win — a caller cannot overwrite what this dialog itself records.
   */
  extraMetadata?: Record<string, unknown>;
}

const LogActivityDialog: React.FC<LogActivityDialogProps> = ({
  open,
  onClose,
  client,
  initialActivityType,
  onSuccess,
  extraMetadata,
}) => {
  const [activityType, setActivityType] = useState(initialActivityType || "");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && initialActivityType) {
      setActivityType(initialActivityType);
    }
  }, [open, initialActivityType]);

  const handleSubmit = async () => {
    if (!client || !activityType) return;
    setLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from("activities").insert({
        user_id: user.id,
        activity_type: activityType,
        entity_type: "client",
        entity_id: client.id,
        metadata: {
          ...(extraMetadata || {}),
          client_name: `${client.first_name} ${client.last_name}`,
          notes: notes || undefined,
        },
      });

      // PKT-0411 (N1): the manual `clients.last_contacted_at` bump that used to
      // live here is GONE. Migration 031's trg_bump_client_last_contacted_on_activity
      // already sets the column from the row we just inserted, and migration 031's
      // own header names removing these manual bumps as the expected follow-through
      // ("Removal of manual last_contacted_at bumps at LogActivityDialog · ClientList
      // · invoices.py"). PKT-0410 did the ClientList half.
      //
      // It was not merely redundant — it was ONE-DIRECTIONAL CLOCK SKEW. The manual
      // write used `new Date()` (the CLIENT's clock); the trigger uses NEW.created_at
      // (the SERVER's) and is forward-only:
      //     AND (last_contacted_at IS NULL OR last_contacted_at < NEW.created_at)
      // A device running fast wrote a FUTURE last_contacted_at, after which the
      // trigger's guard no-ops forever and can never pull it back. That client reads
      // "recently contacted" for longer than the truth and is suppressed from
      // follow-up. The trigger now owns this column outright.

      setActivityType("");
      setNotes("");
      onClose();
      onSuccess();
    } catch (err) {
      console.error("Error logging activity:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActivityType("");
    setNotes("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 7 }}>
        <Tooltip title="Close"><IconButton aria-label="Close" onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8, width: 40, height: 40 }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton></Tooltip>
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon color="primary" />
          Log Activity
        </Box>
      </DialogTitle>
      <DialogContent>
        {client && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Logging activity for{" "}
            <strong>
              {client.first_name} {client.last_name}
            </strong>
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Activity Type *</InputLabel>
            <Select
              value={activityType}
              label="Activity Type *"
              onChange={(e) => setActivityType(e.target.value)}
            >
              {allActivityTypes.map((type) => (
                <MenuItem key={type.key} value={type.key}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {type.icon}
                    {type.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            placeholder="Add any details about this activity..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!activityType || loading}
          startIcon={
            loading ? <CircularProgress size={16} /> : <CheckCircleIcon />
          }
        >
          Log Activity
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogActivityDialog;
